// Everything drawn over the world: the health bar, the purse, the chat log, the
// shop prompt and the respawn notice.
//
// It used to be canvas drawing calls, on the argument that a HUD which scales
// with the framebuffer and one which scales with the page are two interfaces to
// keep in agreement. The cost of that argument was a HUD no screen reader could
// see, no keyboard could reach and no test could read. This is the same HUD in
// DOM, scaled from one custom property the loop sets from the same upscale the
// canvas uses — one interface, still, and now one anybody can use.
//
// The world stays on the canvas. Nameplates, the sword arc and the sparks are
// anchored to positions that move every frame; those are drawing, not UI.

import type { VNode } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { CHAT_MAX_LENGTH } from "../../sim/constants";
import type { Intent } from "../input";
import { CHAT_LINES, promptFor, type HudModel, type Prompt } from "./hud-model";
import { useStore, type Store } from "./store";

export interface HudCommands {
  /** Send a line and give the keyboard back to the game. */
  say(text: string): void;
  /** Give the keyboard back without sending. */
  stopTyping(): void;
  /** Open the chat line. */
  startTyping(): void;
  /** The on-screen pad: the held direction, or zero when it is let go. */
  move(intent: Intent): void;
  attack(): void;
  buy(): void;
}

export function Hud({ store, commands }: { store: Store<HudModel>; commands: HudCommands }): VNode {
  const model = useStore(store);
  if (model.entering) return <Entering />;
  return (
    <div class={model.touch ? "hud touch" : "hud"}>
      <Vitals model={model} />
      <p class="hud-online">{model.online} online</p>
      <PromptLine prompt={promptFor(model)} />
      <Chat model={model} commands={commands} />
      {model.touch && !model.maintenance ? <TouchControls model={model} commands={commands} /> : null}
      {model.maintenance ? <MaintenancePanel message={model.maintenance} /> : null}
      <p class="visually-hidden">
        Arrow keys or W, A, S and D move. Space swings. E buys a potion at the shop. Enter opens the chat line.
      </p>
    </div>
  );
}

/** The one thing on screen before the sprites are baked and the first
 *  snapshot lands. */
export function Entering({ message = "entering the fen…" }: { message?: string } = {}): VNode {
  return (
    <div class="hud">
      <p class="hud-entering" role="status">
        {message}
      </p>
    </div>
  );
}

function Vitals({ model }: { model: HudModel }): VNode {
  const { hp, maxHp, gold, slug } = model;
  return (
    <div class="hud-panel hud-vitals">
      <p class="hud-who">
        <span class="hud-name">player</span>
        <span class="hud-slug">{slug}</span>
      </p>
      {/* The bar is the accessible reading of health; the digits under it are
          the same fact drawn twice, so they are hidden from the tree. */}
      <div
        class="hud-bar"
        role="progressbar"
        aria-label="Health"
        aria-valuemin={0}
        aria-valuemax={maxHp}
        aria-valuenow={hp}
        aria-valuetext={`${hp} of ${maxHp}`}
      >
        <div class="hud-bar-fill" style={{ width: `${(100 * hp) / Math.max(1, maxHp)}%` }} />
      </div>
      <p class="hud-numbers">
        <span class="hud-hp" aria-hidden="true">
          {hp}/{maxHp}
        </span>
        <span class="hud-gold">
          {gold}
          <span aria-hidden="true">g</span>
          <span class="visually-hidden"> gold</span>
        </span>
      </p>
    </div>
  );
}

/** The top strip, said twice: once for eyes and once for a live region.
 *
 *  They are not the same string. The death line counts down every second, and a
 *  live region that says "back in 4, back in 3, back in 2" is a live region
 *  people turn off. */
function PromptLine({ prompt }: { prompt: Prompt | null }): VNode {
  return (
    <>
      <p class="visually-hidden" role="status">
        {prompt?.said ?? ""}
      </p>
      {prompt ? (
        <p class={`hud-panel hud-prompt tone-${prompt.tone}`} aria-hidden="true">
          {prompt.text}
        </p>
      ) : null}
    </>
  );
}

function Chat({ model, commands }: { model: HudModel; commands: HudCommands }): VNode {
  const lines = model.chat.slice(-CHAT_LINES);
  return (
    <div class="hud-panel hud-chat">
      <ol class="hud-log" aria-label="Chat" aria-live="polite" aria-relevant="additions">
        {lines.map((line) => (
          <li key={line.id} class={line.kind === "system" ? "hud-line hud-system" : "hud-line"}>
            {/* The slug is the speaker. Everybody is called "player", so a name
                here would attribute every line in the log to one person. */}
            {line.kind === "say" ? <span class="hud-from">{line.from}</span> : null}
            <span class="hud-said">{line.text}</span>
          </li>
        ))}
      </ol>
      {model.typing ? (
        <ChatComposer commands={commands} />
      ) : model.touch ? null : (
        <p class="hud-hint" aria-hidden="true">
          enter to chat
        </p>
      )}
    </div>
  );
}

/** The chat line, which is a real <input> — so it has paste, an IME, a mobile
 *  keyboard and a caret, none of which the canvas had. */
function ChatComposer({ commands }: { commands: HudCommands }): VNode {
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => field.current?.focus(), []);

  return (
    <form
      class="hud-compose"
      onSubmit={(ev) => {
        ev.preventDefault();
        const text = field.current?.value.trim() ?? "";
        if (field.current) field.current.value = "";
        if (text) commands.say(text);
        else commands.stopTyping();
      }}
    >
      <label class="visually-hidden" for="chat-line">
        Chat message
      </label>
      <input
        id="chat-line"
        ref={field}
        class="hud-field"
        type="text"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck={false}
        maxLength={CHAT_MAX_LENGTH}
        // While this field has the keys, the game must not also have them. The
        // window listener sits above this node, so stopping the bubble here is
        // the whole guard — typing "was it a wisp" cannot walk you into a tree.
        onKeyDown={(ev) => {
          ev.stopPropagation();
          if (ev.key !== "Escape") return;
          ev.preventDefault();
          commands.stopTyping();
        }}
      />
    </form>
  );
}

/** The maintenance lock, over everything. It takes the pointer, so nothing
 *  under it can be pressed while the world applies nothing anyway. */
function MaintenancePanel({ message }: { message: string }): VNode {
  return (
    <div class="hud-maintenance">
      <p class="hud-panel hud-maintenance-text" role="alert">
        {message}
      </p>
    </div>
  );
}

/** How far from the pad's centre, as a share of its radius, a thumb has to be
 *  before it counts as a direction. */
const PAD_DEAD_ZONE = 0.2;

/** The direction a point on the pad means: the axis the thumb is furthest
 *  along, because the world moves on four directions only. */
export function padIntent(dx: number, dy: number, radius: number): Intent {
  if (Math.hypot(dx, dy) < radius * PAD_DEAD_ZONE) return { mx: 0, my: 0 };
  return Math.abs(dx) >= Math.abs(dy) ? { mx: Math.sign(dx), my: 0 } : { mx: 0, my: Math.sign(dy) };
}

/** A pad on the left and the actions on the right, for a touch screen.
 *
 *  The pad follows one pointer from the moment it lands until it lifts, so a
 *  thumb can slide from one direction to the next without lifting. The buttons
 *  are real buttons, so they also work from a keyboard and are announced. */
function TouchControls({ model, commands }: { model: HudModel; commands: HudCommands }): VNode {
  const pad = useRef<HTMLDivElement>(null);
  const steer = (ev: PointerEvent): void => {
    const box = pad.current?.getBoundingClientRect();
    if (!box) return;
    const radius = Math.max(1, box.width / 2);
    commands.move(padIntent(ev.clientX - (box.left + radius), ev.clientY - (box.top + box.height / 2), radius));
  };
  const letGo = (): void => commands.move({ mx: 0, my: 0 });

  return (
    <div class="hud-touch">
      <div
        ref={pad}
        class="hud-pad"
        aria-hidden="true"
        onPointerDown={(ev) => {
          ev.preventDefault();
          // Capture keeps the thumb on the pad when it slides off the edge. A
          // pointer the browser no longer tracks cannot be captured, and the
          // pad still works for that one press without it.
          try {
            pad.current?.setPointerCapture?.(ev.pointerId);
          } catch {
            /* not capturable */
          }
          steer(ev);
        }}
        onPointerMove={(ev) => {
          // Without capture a move counts only while a button is down.
          if (pad.current?.hasPointerCapture?.(ev.pointerId) || ev.buttons > 0) steer(ev);
        }}
        onPointerUp={letGo}
        onPointerCancel={letGo}
        onLostPointerCapture={letGo}
      >
        <span class="hud-pad-arrow up" />
        <span class="hud-pad-arrow right" />
        <span class="hud-pad-arrow down" />
        <span class="hud-pad-arrow left" />
      </div>
      <div class="hud-actions">
        <button type="button" class="hud-button hud-chat-button" onClick={() => commands.startTyping()}>
          chat
        </button>
        {model.atShop ? (
          <button type="button" class="hud-button hud-buy" onClick={() => commands.buy()}>
            buy
          </button>
        ) : null}
        <button type="button" class="hud-button hud-attack" onClick={() => commands.attack()}>
          swing
        </button>
      </div>
    </div>
  );
}
